import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Navigation, Phone, ArrowRight } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { LocationMap } from "@/components/site/LocationMap";
import { Reveal } from "@/components/site/motion";
import { LOCATIONS, findLocation, directionsUrl, telHref, fullAddress } from "@/content/locations";
import { DRINKS, price } from "@/content/menu";
import { LINKS, SITE_URL } from "@/content/site";
import NotFound from "@/pages/NotFound";

const FEATURED = DRINKS.filter((d) => d.image).slice(0, 4);

const jsonLd = (l) => ({
  "@context": "https://schema.org",
  "@type": "CafeOrCoffeeShop",
  name: l.name,
  url: `${SITE_URL}/locations/${l.slug}`,
  telephone: l.phone,
  servesCuisine: ["Bubble tea", "Vietnamese coffee"],
  hasMenu: `${SITE_URL}/menu`,
  image: `${SITE_URL}${l.image}`,
  parentOrganization: { "@type": "Organization", name: "TeaDM", url: SITE_URL },
  address: { "@type": "PostalAddress", streetAddress: l.street, addressLocality: l.city, addressRegion: l.state, postalCode: l.zip, addressCountry: "US" },
  geo: { "@type": "GeoCoordinates", latitude: l.coords[0], longitude: l.coords[1] },
  ...(l.hoursSpec ? { openingHoursSpecification: l.hoursSpec.map((h) => ({ "@type": "OpeningHoursSpecification", dayOfWeek: h.days, opens: h.opens, closes: h.closes })) } : {}),
  potentialAction: { "@type": "OrderAction", target: l.orderUrl },
  sameAs: [LINKS.instagram, LINKS.facebook],
});

export default function LocationDetail() {
  const { slug } = useParams();
  const l = findLocation(slug);
  if (!l) return <NotFound />;
  const isTea = l.brand === "teadm";
  const others = LOCATIONS.filter((x) => x.slug !== l.slug);

  return (
    <>
      <Seo title={l.seoTitle} description={l.seoDesc} path={`/locations/${l.slug}`} jsonLd={jsonLd(l)} />
      <PageHero testId="location-detail-hero" bg={l.color} fg="#1a0a10" eyebrow={`${l.name} · ${l.area}`} lines={[l.city + ",", "Virginia."]} intro={l.info}>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href={l.orderUrl} target="_blank" rel="noopener noreferrer" data-testid="location-detail-order-btn" className="btn btn-ink">Order online from {l.name} <ArrowUpRight className="h-4 w-4" /></a>
          <a href={directionsUrl(l)} target="_blank" rel="noopener noreferrer" data-testid="location-detail-directions-btn" className="btn btn-cream"><Navigation className="h-4 w-4" />Directions</a>
        </div>
        {l.image && <img src={l.image} alt={`TeaDM drink at ${l.name}`} className="pointer-events-none absolute -bottom-24 right-4 hidden h-[420px] w-auto drop-shadow-[0_40px_40px_rgba(0,0,0,0.35)] lg:block" />}
      </PageHero>

      <section className="bg-[#fff5e8] py-20 sm:py-28">
        <div className="wrap grid gap-10 lg:grid-cols-12">
          <Reveal className="space-y-8 lg:col-span-5">
            <div>
              <p className="eyebrow text-[#ff2e63]">Address</p>
              <address className="mt-3 font-display text-3xl font-extrabold not-italic leading-tight" data-testid="location-detail-address">{l.street}<br />{l.city}, {l.state} {l.zip}</address>
            </div>
            <div>
              <p className="eyebrow text-[#ff2e63]">Phone</p>
              <a href={telHref(l.phone)} data-testid="location-detail-phone" className="mt-3 inline-flex items-center gap-2 font-display text-2xl font-bold hover:underline"><Phone className="h-5 w-5" />{l.phone}</a>
            </div>
            <div>
              <p className="eyebrow text-[#ff2e63]">Hours</p>
              {l.hours ? (
                <ul className="mt-3 space-y-2 text-lg" data-testid="location-detail-hours">
                  {l.hours.map((h) => <li key={h.days} className="flex justify-between border-b border-[#1a0a10]/10 pb-2"><span>{h.days}</span><span className="font-bold">{h.time}</span></li>)}
                </ul>
              ) : (
                <p className="mt-3 text-lg" data-testid="location-detail-hours">{l.hoursNote}</p>
              )}
            </div>
          </Reveal>
          <div className="lg:col-span-7"><LocationMap active={l.slug} showPicker={false} className="h-[380px] sm:h-[480px]" /></div>
        </div>
      </section>

      {isTea && (
        <section className="bg-[#efe4d6] py-20 sm:py-28">
          <div className="wrap">
            <div className="mb-10 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
              <h2 className="font-display text-4xl font-extrabold uppercase leading-none tracking-tight sm:text-6xl">Fan favorites in {l.city}</h2>
              <Link to="/menu" data-testid="location-detail-menu-link" className="btn btn-ink self-start">Full menu <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {FEATURED.map((d) => (
                <a key={d.id} href={l.orderUrl} target="_blank" rel="noopener noreferrer" data-testid={`location-featured-${d.id}`} className="group overflow-hidden rounded-[1.8rem] bg-white">
                  <div className="aspect-square overflow-hidden"><img src={d.image} alt={`${d.name} at ${l.name}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                  <div className="p-4"><p className="font-display text-lg font-bold leading-tight">{d.name}</p><p className="text-sm text-[#1a0a10]/70">{price(d.price)}</p></div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#1a0a10] py-20 text-[#fff5e8]">
        <div className="wrap">
          <p className="eyebrow text-[#ffd23f]">Other locations</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((o) => (
              <Link key={o.slug} to={`/locations/${o.slug}`} data-testid={`location-other-${o.slug}`} className="group rounded-[1.6rem] border border-white/10 p-6 transition-colors duration-500 hover:bg-white/5">
                <span className="block h-2 w-10 rounded-full" style={{ background: o.color }} />
                <span className="mt-5 block font-display text-2xl font-extrabold">{o.name}</span>
                <span className="mt-1 block text-sm text-[#fff5e8]/75">{fullAddress(o)}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
